import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserContext } from '@Contexts';
import { UserTypeEnum } from '@Types';
import {
  GroupProfileController,
  FunderProfileController,
  OrganizationController,
  DocumentsController,
  BankDetailsController,
  TeamMembersController,
} from '@Views';
import { useTranslation } from 'react-i18next';
import { AccountNav } from './Components/Nav';
import { PathParams, Routes } from '@App/paths';
import router from 'next/router';
import { useGetCurrentTab } from '@Hooks/useGetCurrentTab';
import { useRouter } from '@Helpers';
import { Button } from '@Components/common';
import { RiArrowLeftLine } from 'react-icons/ri';
import { useTheme, useMediaQuery } from '@material-ui/core';
import { Title } from '@Components/AccountPage/Components/Title';
import styles from './AccountController.module.scss';

export const AccountController = () => {
  const { t } = useTranslation('account');
  const { params } = useRouter();
  const theme = useTheme();
  const screenXs = useMediaQuery(theme.breakpoints.down('xs'));
  const [navOpen, setNavOpen] = useState(false);
  const { userData } = useUserContext();

  const isGroup = useMemo(() => userData?.user_type === UserTypeEnum.GROUP, [userData]);

  useEffect(() => {
    setNavOpen(screenXs);
  }, [screenXs]);

  const tabs = useMemo(() => {
    const funderTabs = [
      { title: `${t('tabs.profile.title')}`, slug: 'profile', amount: undefined },
      { title: `${t('tabs.organization.title')}`, slug: 'organization', amount: undefined },
    ];
    // const teamMembersTab = {
    //   title: `${t('tabs.team_members')}`,
    //   slug: 'team-members',
    //   amount: undefined,
    // };

    if (isGroup) {
      return [
        ...funderTabs,
        { title: `${t('tabs.documents.title')}`, slug: 'documents', amount: undefined },
        { title: `${t('tabs.bank_details.title')}`, slug: 'bank-details', amount: undefined },
      ];
    }
    return funderTabs;
  }, [t, isGroup]);

  const currentTab = useGetCurrentTab(params[PathParams.ACCOUNT_SLUG]?.[0], tabs);
  const setTab = useCallback(
    (tabNumber: number) => {
      const route = Routes.ACCOUNT(tabs[tabNumber].slug);
      setNavOpen(false);
      return router.push(route, undefined, {
        shallow: true,
      });
    },
    [tabs],
  );

  const handleOpenNav = useCallback(() => {
    setNavOpen(true);
  }, []);

  return (
    <>
      <AccountNav
        accountNavItems={tabs}
        currentTab={currentTab}
        setTab={setTab}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
      />
      <div className={styles.accountContainer}>
        {screenXs && (
          <Button buttonType="text" onClick={handleOpenNav} startIcon={<RiArrowLeftLine />}>
            {t('back')}
          </Button>
        )}

        {currentTab == 0 && (
          <>
            {userData?.user_type === UserTypeEnum.GROUP ? (
              <>
                <Title title={t('tabs.profile.title')} />
                <GroupProfileController />
              </>
            ) : (
              <>
                <Title title={t('tabs.profile.title')} />
                <FunderProfileController />
              </>
            )}
          </>
        )}
        {currentTab == 1 && (
          <>
            <Title title={t('tabs.organization.title')} isPrivate />
            <OrganizationController />
          </>
        )}
        {currentTab == 2 && isGroup && (
          <>
            <Title title={t('tabs.documents.title')} isPrivate />
            <DocumentsController />
          </>
        )}
        {currentTab == 3 && isGroup && (
          <>
            <Title title={t('tabs.bank_details.title')} isPrivate />
            <BankDetailsController />
          </>
        )}
        {/* {((currentTab == 4 && isGroup) || (currentTab == 1 && !isGroup)) && (
          <TeamMembersController />
        )} */}
      </div>
    </>
  );
};
